import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  BellRing,
  Gift,
  Headphones,
  Heart,
  LogOut,
  Megaphone,
  MessageSquare,
  Package,
  ShieldCheck,
  UserRound,
  Wallet,
} from "lucide-react";
import { Avatar } from "@/components/ui";
import { useAuth } from "@/stores/auth";
import { toast } from "@/stores/toast";

export function UserMenu() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!user) return null;
  const item = "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ink-soft hover:bg-fg/5 hover:text-ink";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="ml-1 flex shrink-0 items-center gap-2 rounded-full p-0.5 ring-1 ring-fg/10 transition hover:ring-[var(--border-glow)] md:pr-3"
      >
        <Avatar name={user.full_name} src={user.avatar || null} size="sm" />
        <span className="hidden max-w-[120px] truncate text-sm text-ink-soft md:inline">{user.first_name || user.full_name}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 max-h-[calc(100dvh-var(--app-top)-24px)] w-60 max-w-[calc(100vw-24px)] animate-pop-in overflow-y-auto rounded-card border border-[var(--border-glow)] bg-elevated p-1.5 shadow-glow-2"
        >
          <div className="border-b border-line px-3 pb-3 pt-2">
            <p className="truncate text-sm font-medium text-ink">{user.full_name}</p>
            <p className="truncate text-xs text-ink-muted">{user.email}</p>
          </div>
          {user.is_staff && (
            <Link role="menuitem" to="/admin" className={item} onClick={() => setOpen(false)}>
              <ShieldCheck className="size-4" aria-hidden /> Admin console
            </Link>
          )}
          <Link role="menuitem" to="/account" className={item} onClick={() => setOpen(false)}>
            <UserRound className="size-4" aria-hidden /> My account
          </Link>
          <Link role="menuitem" to="/account/orders" className={item} onClick={() => setOpen(false)}>
            <Package className="size-4" aria-hidden /> Orders
          </Link>
          <Link role="menuitem" to="/account/wishlist" className={item} onClick={() => setOpen(false)}>
            <Heart className="size-4" aria-hidden /> Saved items
          </Link>
          <Link role="menuitem" to="/account/wallet" className={item} onClick={() => setOpen(false)}>
            <Wallet className="size-4" aria-hidden /> Wallet
          </Link>
          <Link role="menuitem" to="/ads" className={item} onClick={() => setOpen(false)}>
            <Megaphone className="size-4" aria-hidden /> Your ads
          </Link>
          <Link role="menuitem" to="/chats" className={item} onClick={() => setOpen(false)}>
            <MessageSquare className="size-4" aria-hidden /> Messages
          </Link>
          <Link role="menuitem" to="/notifications" className={item} onClick={() => setOpen(false)}>
            <Bell className="size-4" aria-hidden /> Notifications
          </Link>
          <Link role="menuitem" to="/account/rewards" className={item} onClick={() => setOpen(false)}>
            <Gift className="size-4" aria-hidden /> Rewards & referrals
          </Link>
          <Link role="menuitem" to="/account/alerts" className={item} onClick={() => setOpen(false)}>
            <BellRing className="size-4" aria-hidden /> Alerts
          </Link>
          <Link role="menuitem" to="/support" className={item} onClick={() => setOpen(false)}>
            <Headphones className="size-4" aria-hidden /> Help & support
          </Link>
          <button
            role="menuitem"
            type="button"
            className={`${item} w-full`}
            onClick={async () => {
              setOpen(false);
              await logout();
              toast.info("You're logged out", "See you soon.");
              navigate("/");
            }}
          >
            <LogOut className="size-4" aria-hidden /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
